/**
 * Carbon's condensed grid (DESIGN.md "The Gutter Rule"): related tiles sit
 * flush with 1px gutters that show the border-subtle color behind them,
 * inside a 1px border-subtle frame. Children bring their own layer-01 fill
 * (a Tile). Columns are mobile-first per Carbon breakpoint; the Command
 * Center pillar row is `{ base: 1, 'c-md': 2, 'c-xlg': 4 }`.
 */
import clsx from 'clsx'
import type { CondensedGridProps, GridColumnCount as Cols } from './types'

// Literal class names so Tailwind's content scan finds every variant.
const BASE: Record<Cols, string> = {
  1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4', 5: 'grid-cols-5', 6: 'grid-cols-6',
}
const MD: Record<Cols, string> = {
  1: 'c-md:grid-cols-1', 2: 'c-md:grid-cols-2', 3: 'c-md:grid-cols-3', 4: 'c-md:grid-cols-4', 5: 'c-md:grid-cols-5', 6: 'c-md:grid-cols-6',
}
const LG: Record<Cols, string> = {
  1: 'c-lg:grid-cols-1', 2: 'c-lg:grid-cols-2', 3: 'c-lg:grid-cols-3', 4: 'c-lg:grid-cols-4', 5: 'c-lg:grid-cols-5', 6: 'c-lg:grid-cols-6',
}
const XLG: Record<Cols, string> = {
  1: 'c-xlg:grid-cols-1', 2: 'c-xlg:grid-cols-2', 3: 'c-xlg:grid-cols-3', 4: 'c-xlg:grid-cols-4', 5: 'c-xlg:grid-cols-5', 6: 'c-xlg:grid-cols-6',
}

export function CondensedGrid({ columns = { base: 1 }, as: Element = 'div', className, children, ...rest }: CondensedGridProps) {
  return (
    <Element
      className={clsx(
        'm-0 grid list-none gap-px border border-line-subtle bg-line-subtle p-0',
        BASE[columns.base],
        columns['c-md'] && MD[columns['c-md']],
        columns['c-lg'] && LG[columns['c-lg']],
        columns['c-xlg'] && XLG[columns['c-xlg']],
        className,
      )}
      {...rest}
    >
      {children}
    </Element>
  )
}

export default CondensedGrid
